import React, { useState, useEffect, useRef } from 'react';
import { tasksApi } from '../../../../features/tasks/api';
import { useAllUsersIncludingArchived } from '../../../hooks/useAllUsers';

// 02.9-shared-widgets.md paso b (§5) -- extraido de InlineSocialOutline.jsx. `toast` llega por
// prop (era un `useToast()` propio, §0.2/§0.4): a diferencia del padre, este SI es un hijo React
// real (no un createRoot separado), asi que threadear el valor vivo del padre es lo correcto.
// La lista de usuarios usa `useAllUsersIncludingArchived()` (era un `fetch('/api/users?all=true')`
// a mano) -- la variante "archived-inclusive", no `useAllUsers()`, porque el picker de
// encargado/supervisor ya incluia cuentas archivadas antes de este fix (§0.4).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: resetea el índice de usuario seleccionado al cambiar de paso del wizard.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const TaskWizard = ({ onComplete, onCancel, spaceId, toast, docId }) => {
    const [step, setStep] = useState(0); // 0:Name, 1:Assignee, 2:Desc, 3:Time, 4:Supervisor
    const [data, setData] = useState({ name: '', assignee: '', desc: '', time: '', supervisor: '' });
    const inputRef = useRef(null);
    const { data: users = [] } = useAllUsersIncludingArchived();

    const [selectedUserIndex, setSelectedUserIndex] = useState(0);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
        setSelectedUserIndex(0);
    }, [step]);

    const keys = ['name', 'assignee', 'desc', 'time', 'supervisor'];
    const currentKey = keys[step];
    const isUserField = step === 1 || step === 4;

    const filteredUsers = isUserField && data[currentKey]
        ? users.filter(u => (u.nick || u.name || u.username || '').toLowerCase().includes(data[currentKey].toLowerCase())).slice(0, 5)
        : (isUserField ? users.slice(0, 5) : []);

    const submitTask = async (finalData) => {
        try {
            const taskDurationMins = parseInt(finalData.time) || 60;
            const now = new Date();
            const endTime = new Date(now.getTime() + taskDurationMins * 60000);

            // 02.9-shared-widgets.md paso b (§0.4) -- reemplaza el `fetch('/api/tasks', ...)` a
            // mano (import prohibido implicito, duplicaba el endpoint que tasksApi ya cubre) por
            // tasksApi.createTask, ya usado por handleRapidTaskSubmit en el padre.
            const response = await tasksApi.createTask({
                title: finalData.name,
                assignee: finalData.assignee,
                description: finalData.desc,
                duration_minutes: taskDurationMins,
                start_time: now.toISOString(),
                end_time: endTime.toISOString(),
                supervisor: finalData.supervisor,
                space_id: spaceId === 'all' ? null : spaceId,
                ...(docId ? { doc_id: docId } : {})
            });
            if (response?.success !== false) {
                toast.success("Tarea auto-programada");
                onComplete({ name: finalData.name, assignee: finalData.assignee, taskId: response?.task?.id, status: 'pending', start_time: response?.task?.start_time || now.toISOString() });
            } else {
                toast.error(response.error || "Error creando tarea");
                onCancel();
            }
        } catch {
            toast.error("Error de conexión");
            onCancel();
        }
    };

    const handleKeyDown = async (e) => {
        if (e.key === 'Escape') {
            onCancel();
            return;
        }

        if (isUserField && filteredUsers.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedUserIndex(prev => (prev + 1) % filteredUsers.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedUserIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
                return;
            }
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (isUserField && filteredUsers.length > 0) {
                const selected = filteredUsers[selectedUserIndex];
                const val = selected.nick || selected.name || selected.username;
                const newData = { ...data, [currentKey]: val };
                setData(newData);
                if (step < 4) setStep(step + 1);
                else submitTask(newData);
            } else {
                if (!data[currentKey] && !isUserField) return; // name is required
                if (step < 4) setStep(step + 1);
                else submitTask(data);
            }
        }
    };

    const handleUserClick = (u) => {
        const val = u.nick || u.name || u.username;
        const newData = { ...data, [currentKey]: val };
        setData(newData);
        if (step < 4) setStep(step + 1);
        else submitTask(newData);
    };

    const placeholders = ['Nombre de tarea...', 'Encargado...', 'Descripción...', 'Tiempo (minutos)...', 'Supervisor...'];

    return (
        <div className="iso-wizard" style={{ position: 'relative' }}>
            <span className="iso-wizard-label">{step+1}/5</span>
            <input
                ref={inputRef}
                className="iso-wizard-input"
                placeholder={placeholders[step]}
                value={data[currentKey]}
                onChange={e => {
                    setData({...data, [currentKey]: e.target.value});
                    setSelectedUserIndex(0);
                }}
                onKeyDown={handleKeyDown}
                autoComplete="off"
            />
            <span className="iso-wizard-hint">Enter para seguir</span>

            {step === 3 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, display: 'flex', gap: '6px', padding: '6px', margin: '4px 0 0 0', backgroundColor: '#222', borderRadius: '6px', border: '1px solid #333', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.5)', overflowX: 'auto' }}>
                    {[
                        { label: '5 min', val: 5 },
                        { label: '20min', val: 20 },
                        { label: '40 min', val: 40 },
                        { label: '3horas', val: 180 },
                        { label: '7horas', val: 420 }
                    ].map(tag => (
                        <div
                            key={tag.val}
                            onClick={() => {
                                const newData = { ...data, time: tag.val.toString() };
                                setData(newData);
                                setStep(step + 1);
                            }}
                            style={{ padding: '4px 10px', backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff', borderRadius: 'var(--radius-lg)', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.1)' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                        >
                            {tag.label}
                        </div>
                    ))}
                </div>
            )}

            {isUserField && (
                <ul className="iso-user-dropdown">
                    {filteredUsers.length > 0 ? filteredUsers.map((u, i) => (
                        <li
                            key={u.id}
                            className={`iso-user-dropdown-item ${i === selectedUserIndex ? 'active' : ''}`}
                            onMouseEnter={() => setSelectedUserIndex(i)}
                            onClick={() => handleUserClick(u)}
                        >
                            <img
                                src={(u['link-profile-img-thumb'] || u['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.nick || u.name}`}
                                alt={u.nick || u.name}
                                loading="lazy"
                            />
                            <span>{u.nick || u.name || u.username}</span>
                        </li>
                    )) : (
                        <li className="iso-user-dropdown-empty">No se encontraron usuarios</li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default TaskWizard;
